import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Checkbox,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Button,
  Skeleton,
  Chip,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Alert,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { openContactInInbox } from "../../../utils/openContactInInbox";
import {
  fetchContacts,
  setSelectedContacts,
  setAllCurrentContacts,
  setCurrentPage,
  setSortField,
  setSortDirection,
  updateContact,
} from "../../../redux/Slice/Contacts/contactsSlice.js";
import {
  fetchContactGroups,
} from "../../../redux/Slice/Contacts/contactGroupsSlice";
import SendIcon from "@mui/icons-material/Send";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AddIcon from "@mui/icons-material/Add";
import LabelsDialog from "./LabelsDialog";
import LabelsFilter from "./LabelsFilter";
import AddContact from "./AddContact";
import { useSubscriptionGuard } from "../../../hooks/useSubscriptionGuard";

// Helper function to highlight text
const HighlightedText = ({ text, searchTerm }) => {
  const theme = useTheme();
  
  if (!searchTerm || !text) return text;

  const parts = text.split(new RegExp(`(${searchTerm})`, "gi"));

  return (
    <span>
      {parts.map((part, i) =>
        part.toLowerCase() === searchTerm.toLowerCase() ? (
          <span
            key={i}
            style={{
              backgroundColor: theme.palette.mode === "dark" 
                ? "rgba(255, 152, 0, 0.4)" // Dark orange with transparency for dark mode
                : "#ffeb3b", // Yellow for light mode
              padding: "0 2px",
              borderRadius: "2px",
            }}
          >
            {part}
          </span>
        ) : (
          part
        )
      )}
    </span>
  );
};

const ContactsList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.only("xs")); // 0-599px
  const isXl = useMediaQuery(theme.breakpoints.up("xl")); // 1536px+
  
  const { userDetails } = useSelector((state) => state.userDetails);
  const { contacts, selectedContacts, loading, searchTerm, statusFilter, pagination, sortField, sortDirection } =
    useSelector((state) => state.contacts);
  const { groups } = useSelector((state) => state.contactGroups);
  const { labelFilter } = useSelector((state) => state.labels);
  const ContactPermissions = useSelector(
    (state) => state.userDetails.userPermissions?.permissions?.Contact || {}
  );
  const [isAllChecked, setIsAllChecked] = useState(false);
  const [labelsDialogOpen, setLabelsDialogOpen] = useState(false);
  
  // Edit contact modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [editFormData, setEditFormData] = useState({ name: "", phoneNumberID: "" });
  
  // Success toast state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Create Contact modal state for xs screens
  const [isCreateContactModalOpen, setIsCreateContactModalOpen] = useState(false);

  const { guard } = useSubscriptionGuard();

  const handleCreateContact = async () => {
    if (!await guard(['contacts'])) return;
    setIsCreateContactModalOpen(true);
  };

  const filtersKeyRef = useRef("");

  // Contact groups are global to the list — load once, not on every contacts page change
  useEffect(() => {
    dispatch(fetchContactGroups());
  }, [dispatch]);

  // Reset to page 1 only when search / status / labels filters actually change (not when pagination alone changes)
  useEffect(() => {
    const key = `${searchTerm}|${statusFilter}|${JSON.stringify(labelFilter)}`;
    if (filtersKeyRef.current !== "" && filtersKeyRef.current !== key) {
      dispatch(setCurrentPage(1));
    }
    filtersKeyRef.current = key;
  }, [searchTerm, statusFilter, labelFilter, dispatch]);

  // Fetch contacts when component mounts or when pagination / filters / sort change
  useEffect(() => {
    dispatch(fetchContacts({
      page: pagination.currentPage,
      limit: pagination.limit,
      search: searchTerm,
      status: statusFilter,
      labels: labelFilter,
      sortField,
      sortDirection
    }));
  }, [dispatch, pagination.currentPage, pagination.limit, searchTerm, statusFilter, labelFilter, sortField, sortDirection]);

  const handleSort = (field) => {
    if (sortField === field) {
      // Toggle direction if same field
      dispatch(setSortDirection(sortDirection === "asc" ? "desc" : "asc"));
    } else {
      // New field, set to ascending
      dispatch(setSortField(field));
      dispatch(setSortDirection("asc"));
    }
  };

  // Update pagination handlers for TablePagination
  const handlePageChange = (event, newPage) => {
    dispatch(setCurrentPage(newPage + 1)); // Convert to 1-based indexing for API
  };

  const handleRowsPerPageChange = (event) => {
    const newLimit = parseInt(event.target.value, 10);
    dispatch(setCurrentPage(1)); // Reset to first page when changing limit
    // Update the limit in the API call
    dispatch(fetchContacts({ 
      page: 1, 
      limit: newLimit,
      search: searchTerm,
      status: statusFilter,
      labels: labelFilter,
      sortField,
      sortDirection
    }));
  };

  // Use contacts directly from Redux state (server-side filtering)
  const currentContacts = contacts;

  const handleCheckboxChange = (contactId) => {
    dispatch(setSelectedContacts(contactId));
  };

  const handleSelectAllChange = (event) => {
    const isChecked = event.target.checked;
    setIsAllChecked(isChecked);
    const currentContactIds = currentContacts.map((contact) => contact._id);

    if (isChecked) {
      const currentContactIds = currentContacts.map((contact) => contact._id);
      dispatch(
        setAllCurrentContacts({ ids: currentContactIds, isRemove: false })
      ); // For removing
    } else {
      dispatch(
        setAllCurrentContacts({ ids: currentContactIds, isRemove: true })
      ); // For adding
    }
  };


  const handleLabelsDialogSuccess = () => {
    // Refresh contacts to show updated labels
    dispatch(fetchContacts({ 
      page: pagination.currentPage, 
      limit: pagination.limit,
      search: searchTerm,
      status: statusFilter,
      labels: labelFilter,
      sortField,
      sortDirection
    }));
  };

  const handleLabelFilterChange = (filters) => {
    // This will be handled by the LabelsFilter component through Redux
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A"; // Handle missing values

    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "Invalid Date"; // Handle invalid dates

    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "short",
      year: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    }).format(date);
  };

  const handleSendMessage = (contact) => {
    if (!userDetails || !contact || !contact._id) return;

    openContactInInbox({
      dispatch, navigate,
      accountid: contact.AccountID,
      userid: userDetails._id,
      contactId: contact._id,
    });
  };

  // Edit contact handlers
  const handleEditClick = async (contact) => {
    if (!await guard([])) return;
    setEditingContact(contact);
    setEditFormData({
      name: contact.name || "",
      phoneNumberID: contact.platform === "instagram" ? (contact.IGid || "") : (contact.phoneNumberID || ""),
    });
    setEditModalOpen(true);
  };

  const handleEditSubmit = async () => {
    if (!editFormData.name.trim()) return;

    const updateData =
      editingContact?.platform === "instagram" || editingContact?.platform === "facebook"
        ? { name: editFormData.name }
        : editFormData;

    try {
      await dispatch(
        updateContact({
          id: editingContact._id,
          updateData,
        })
      ).unwrap();
      
      setEditModalOpen(false);
      setEditingContact(null);
      setEditFormData({ name: "", phoneNumberID: "" });
      
      // Show success toast
      setSnackbar({
        open: true,
        message: "Contact updated successfully!",
        severity: "success",
      });
      
      // Refresh contacts to show updated data
      dispatch(fetchContacts({ 
        page: pagination.currentPage, 
        limit: pagination.limit,
        search: searchTerm,
        status: statusFilter,
        labels: labelFilter,
        sortField,
        sortDirection
      }));
    } catch (error) {
      console.error("Failed to update contact:", error);
      setSnackbar({
        open: true,
        message: "Failed to update contact",
        severity: "error",
      });
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Box sx={{ 
      width: "100%",
      maxWidth: "100%",
      padding: { xs: 0, sm: 0, md: 0, lg: 0, xl: 0 }, 
      paddingLeft: { xs: 0, sm: 0, md: 0, lg: 0, xl: 0 },
      paddingRight: { xs: 0, sm: 0, md: 0, lg: 0, xl: 0 },
      boxSizing: "border-box",
      display: "flex",
      flexDirection: "column",
      flex: 1,
      minHeight: 0,
      overflow: "hidden",
    }}>
      {/* Labels Filter - Only show on xl screens */}
      {isXl && (
        <Box sx={{ mb: { xs: 1, lg: 1.5, xl: 2 }, p: { xs: 1, lg: 1.5, xl: 2 }, borderBottom: 1, borderColor: 'divider' }}>
          <LabelsFilter onFilterChange={handleLabelFilterChange} />
        </Box>
      )}

      <Paper
        sx={{
          width: { xs: "100%", sm: "100%", md: "100%", lg: "100%", xl: "100%" },
          maxWidth: "100%",
          marginRight: { xs: 0, sm: 0, md: 0, lg: 0, xl: 0 },
          marginLeft: { xs: 0, sm: 0, md: 0, lg: 0, xl: 0 },
          overflow: { xs: "hidden", sm: "hidden", md: "hidden", lg: "hidden", xl: "hidden" },
          borderRadius: { xs: 1, sm: 1.5, md: 2, lg: 2, xl: 3 },
          backgroundColor: (theme) => theme.palette.background.paper,
          boxSizing: "border-box",
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          boxShadow: {
            xs: "none",
            sm: "none",
            md: (theme) =>
              theme.palette.mode === "dark"
                ? "0 8px 28px rgba(0, 0, 0, 0.45)"
                : "0 6px 20px rgba(0, 0, 0, 0.08)",
            lg: (theme) =>
              theme.palette.mode === "dark"
                ? "0 8px 28px rgba(0, 0, 0, 0.45)"
                : "0 6px 20px rgba(0, 0, 0, 0.08)",
            xl: (theme) =>
              theme.palette.mode === "dark"
                ? "0 8px 28px rgba(0, 0, 0, 0.45)"
                : "0 6px 20px rgba(0, 0, 0, 0.08)",
          },
        }}
      >
        <TableContainer 
          sx={{ 
            flex: 1,
            height: 0,
            minHeight: 0,
            overflowX: { xs: "auto", sm: "auto", md: "auto", lg: "auto", xl: "auto" },
            overflowY: "auto",
            width: { xs: "calc(100% - 2px)", sm: "100%", md: "100%", lg: "100%", xl: "100%" },
            maxWidth: "100%",
            boxSizing: "border-box",
            marginBottom: { xs: 0, sm: 0, md: 0, lg: 0, xl: 0 },
            marginRight: { xs: "2px", sm: 0, md: 0, lg: 0, xl: 0 },
            "&::-webkit-scrollbar": {
              width: { xs: "4px", sm: "4px", md: "4px", lg: "4px", xl: "4px" },
              height: { xs: "4px", sm: "4px", md: "4px", lg: "4px", xl: "4px" },
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: (theme) => theme.palette.mode === "dark" ? "#1E1E1E" : "#f1f1f1",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: (theme) => theme.palette.mode === "dark" ? "#888" : "#888",
              borderRadius: "4px",
              "&:hover": {
                backgroundColor: (theme) => theme.palette.mode === "dark" ? "#aaa" : "#aaa",
              },
            },
            "& .MuiTable-root": {
              width: "100%",
              tableLayout: "auto",
            },
          }}
        >
          <Table stickyHeader aria-label="contacts table" sx={{ 
            minWidth: { xs: 720, sm: 800, md: 600, lg: 600, xl: "auto" },
            width: "100%",
            tableLayout: "auto",
          }}>
            <TableHead>
              <TableRow>
                <TableCell
                  padding="checkbox"
                  sx={{
                    backgroundColor: (theme) => theme.palette.background.paper,
                    padding: { xs: "4px 0px 4px 2px", sm: "6px 3px", md: "8px 4px", lg: "10px 5px", xl: "16px 8px" },
                    width: { xs: 32, sm: "auto", md: "auto", lg: "auto", xl: "auto" },
                  }}
                >
                  <Checkbox
                    inputProps={{ "aria-label": "select all contacts" }}
                    checked={isAllChecked}
                    onChange={handleSelectAllChange}
                    size={isXl ? "medium" : "small"}
                    sx={{
                      color: (theme) => theme.palette.text.secondary,
                      padding: { xs: 0, sm: "3px", md: "4px", lg: "6px", xl: "8px" },
                      "& .MuiSvgIcon-root": {
                        fontSize: { xs: "12px", sm: "16px", md: "18px", lg: "18px", xl: "24px" },
                      },
                      "&.Mui-checked": {
                        color: (theme) => theme.palette.primary.main,
                      },
                    }}
                  />
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: (theme) => theme.palette.background.paper,
                    padding: { xs: "6px 6px", sm: "8px 6px", md: "8px 4px", lg: "10px 5px", xl: "16px 8px" },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      cursor: "pointer",
                      whiteSpace: { xs: "nowrap", sm: "normal" },
                      "&:hover": { opacity: 0.7 },
                    }}
                    onClick={() => handleSort("name")}
                  >
                    <Typography 
                      fontWeight="bold"
                      sx={{ fontSize: { xs: "12px", sm: "11px", md: "10px", lg: "11px", xl: "14px" }, whiteSpace: { xs: "nowrap", sm: "normal" } }}
                    >
                      Name
                    </Typography>
                    {sortField === "name" && sortDirection === "asc" ? (
                      <ArrowUpwardIcon sx={{ ml: 0.25, fontSize: { xs: "14px", sm: "14px", md: "14px", lg: "16px", xl: "18px" } }} />
                    ) : sortField === "name" && sortDirection === "desc" ? (
                      <ArrowDownwardIcon sx={{ ml: 0.25, fontSize: { xs: "14px", sm: "14px", md: "14px", lg: "16px", xl: "18px" } }} />
                    ) : null}
                  </Box>
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: (theme) => theme.palette.background.paper,
                    padding: { xs: "6px 6px", sm: "8px 6px", md: "8px 4px", lg: "10px 5px", xl: "16px 8px" },
                  }}
                >
                  <Typography 
                    fontWeight="bold"
                    sx={{ fontSize: { xs: "12px", sm: "11px", md: "10px", lg: "11px", xl: "14px" }, whiteSpace: { xs: "nowrap", sm: "normal" } }}
                  >
                    Phone no
                  </Typography>
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: (theme) => theme.palette.background.paper,
                    padding: { xs: "6px 6px", sm: "8px 6px", md: "8px 4px", lg: "10px 5px", xl: "16px 8px" },
                    width: { xs: "auto", sm: "auto", md: "auto", lg: "auto", xl: "auto" },
                  }}
                >
                  <Typography 
                    fontWeight="bold"
                    sx={{ fontSize: { xs: "12px", sm: "11px", md: "10px", lg: "11px", xl: "14px" }, whiteSpace: { xs: "nowrap", sm: "normal" } }}
                  >
                    Status
                  </Typography>
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: (theme) => theme.palette.background.paper,
                    padding: { xs: "4px 1px", sm: "6px 2px", md: "8px 4px", lg: "10px 5px", xl: "16px 8px" },
                    textAlign: "center",
                    width: { xs: "auto", sm: "auto", md: "auto", lg: "auto", xl: "auto" },
                    display: { xs: "table-cell", sm: "none" },
                  }}
                >
                  <Typography
                    fontWeight="bold"
                    sx={{ fontSize: { xs: "8px", sm: "9px", md: "10px", lg: "11px", xl: "14px" } }}
                  >
                    Actions
                  </Typography>
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: (theme) => theme.palette.background.paper,
                    padding: { xs: "6px 6px", sm: "8px 6px", md: "8px 4px", lg: "10px 5px", xl: "16px 8px" },
                  }}
                >
                  <Typography 
                    fontWeight="bold"
                    sx={{ fontSize: { xs: "12px", sm: "11px", md: "10px", lg: "11px", xl: "14px" }, whiteSpace: { xs: "nowrap", sm: "normal" } }}
                  >
                    Label
                  </Typography>
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: (theme) => theme.palette.background.paper,
                    padding: { xs: "6px 6px", sm: "8px 6px", md: "8px 4px", lg: "10px 5px", xl: "16px 8px" },
                  }}
                >
                  <Typography 
                    fontWeight="bold"
                    sx={{ fontSize: { xs: "12px", sm: "11px", md: "10px", lg: "11px", xl: "14px" }, whiteSpace: { xs: "nowrap", sm: "normal" } }}
                  >
                    Created On
                  </Typography>
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: (theme) => theme.palette.background.paper,
                    padding: { xs: "4px 1px", sm: "6px 2px", md: "8px 4px", lg: "10px 5px", xl: "16px 8px" },
                    textAlign: "center",
                    width: { xs: "auto", sm: "auto", md: "auto", lg: "auto", xl: "auto" },
                    display: { xs: "none", sm: "table-cell" },
                  }}
                >
                  <Typography 
                    fontWeight="bold"
                    sx={{ fontSize: { xs: "8px", sm: "9px", md: "10px", lg: "11px", xl: "14px" } }}
                  >
                    Actions
                  </Typography>
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: (theme) => theme.palette.background.paper,
                    padding: { xs: "6px 6px", sm: "8px 6px", md: "8px 4px", lg: "10px 5px", xl: "16px 8px" },
                  }}
                >
                  <Typography 
                    fontWeight="bold"
                    sx={{ fontSize: { xs: "12px", sm: "11px", md: "10px", lg: "11px", xl: "14px" }, whiteSpace: { xs: "nowrap", sm: "normal" } }}
                  >
                    Groups
                  </Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                // Skeleton rows during loading
                [...Array(pagination.limit || 10)].map((_, index) => (
                  <TableRow key={`skeleton-${index}`}>
                    <TableCell padding="checkbox" sx={{ padding: "16px 8px" }}>
                      <Skeleton variant="circular" width={24} height={24} />
                    </TableCell>
                    <TableCell sx={{ padding: "16px 8px" }}>
                      <Skeleton variant="text" width={120} />
                    </TableCell>
                    <TableCell sx={{ padding: "16px 8px" }}>
                      <Skeleton variant="text" width={120} />
                    </TableCell>
                    <TableCell sx={{ padding: "16px 8px" }}>
                      <Skeleton variant="text" width={80} />
                    </TableCell>
                    <TableCell sx={{ padding: "16px 8px" }}>
                      <Skeleton variant="text" width={100} />
                    </TableCell>
                    <TableCell sx={{ padding: "16px 8px" }}>
                      <Skeleton variant="text" width={80} />
                    </TableCell>
                    <TableCell sx={{ padding: "16px 8px" }}>
                      <Skeleton variant="rectangular" width={120} height={36} />
                    </TableCell>
                    <TableCell sx={{ padding: "16px 8px" }}>
                      <Skeleton variant="text" width={80} />
                    </TableCell>
                  </TableRow>
                ))
              ) : currentContacts.length === 0 ? (
                // Empty state
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ padding: "32px 16px" }}>
                    <Typography variant="body1" color="textSecondary">
                      No contacts found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                // Actual contact rows
                currentContacts.map((contact) => (
                <TableRow
                  key={contact._id}
                  sx={{
                    minHeight: { xs: 0, sm: 44 },
                    "&:hover": {
                      backgroundColor: (theme) =>
                        theme.palette.mode === "dark"
                          ? "rgba(255, 255, 255, 0.08)"
                          : "rgba(0, 0, 0, 0.04)",
                    },
                  }}
                >
                  <TableCell 
                    padding="checkbox" 
                    sx={{
                      padding: { xs: "4px 0 4px 2px", sm: "6px 3px", md: "8px 4px", lg: "10px 5px", xl: "16px 8px" },
                      width: { xs: 32, sm: "auto", md: "auto", lg: "auto", xl: "auto" },
                      maxWidth: { xs: 36, sm: "none", md: "none", lg: "none", xl: "none" },
                      verticalAlign: "middle",
                    }}
                  >
                    <Checkbox
                      checked={selectedContacts.includes(contact._id)}
                      onChange={() => handleCheckboxChange(contact._id)}
                      size={isXl ? "medium" : "small"}
                      sx={{
                        color: (theme) => theme.palette.text.secondary,
                        padding: { xs: 0, sm: "3px", md: "4px", lg: "6px", xl: "8px" },
                        "& .MuiSvgIcon-root": {
                          fontSize: { xs: "12px", sm: "16px", md: "18px", lg: "18px", xl: "24px" },
                        },
                        "&.Mui-checked": {
                          color: (theme) => theme.palette.primary.main,
                        },
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ padding: { xs: "6px 6px", sm: "8px 6px", md: "8px 4px", lg: "10px 5px", xl: "16px 8px" }, verticalAlign: "middle" }}>
                    <Typography sx={{ fontSize: { xs: "12px", sm: "11px", md: "10px", lg: "11px", xl: "14px" }, whiteSpace: { xs: "nowrap", sm: "normal" }, lineHeight: { xs: 1.25, sm: 1.43 } }}>
                      <HighlightedText
                        text={contact.name}
                        searchTerm={searchTerm}
                      />
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ padding: { xs: "6px 6px", sm: "8px 6px", md: "8px 4px", lg: "10px 5px", xl: "16px 8px" }, verticalAlign: "middle" }}>
                    <Typography sx={{ fontSize: { xs: "12px", sm: "11px", md: "10px", lg: "11px", xl: "14px" }, whiteSpace: { xs: "nowrap", sm: "normal" }, lineHeight: { xs: 1.25, sm: 1.43 } }}>
                      <HighlightedText
                        text={contact.platform === "facebook" ? "Facebook" : contact.platform === "instagram" ? "Instagram" : (contact.phoneNumberID || "N/A")}
                        searchTerm={searchTerm}
                      />
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ padding: { xs: "6px 6px", sm: "8px 6px", md: "8px 4px", lg: "10px 5px", xl: "16px 8px" }, verticalAlign: "middle" }}>
                    <Typography sx={{ fontSize: { xs: "12px", sm: "11px", md: "10px", lg: "11px", xl: "14px" }, whiteSpace: { xs: "nowrap", sm: "normal" }, lineHeight: { xs: 1.25, sm: 1.43 } }}>
                      <HighlightedText
                        text={contact.status}
                        searchTerm={searchTerm}
                      />
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ padding: { xs: "6px 4px", sm: "6px 2px", md: "8px 4px", lg: "10px 5px", xl: "16px 8px" }, textAlign: "center", verticalAlign: "middle", display: { xs: "table-cell", sm: "none" } }}>
                    <Box sx={{ display: "flex", gap: { xs: 0.15, sm: 0.4, md: 0.5, lg: 0.6, xl: 1 }, alignItems: "center", justifyContent: "center", flexWrap: { xs: "nowrap", sm: "wrap", lg: "nowrap" } }}>
                      <Tooltip title="View/Edit Contact">
                        <IconButton
                          size={isXl ? "medium" : "small"}
                          onClick={() => handleEditClick(contact)}
                          sx={{
                            color: (theme) => theme.palette.primary.main,
                            padding: { xs: "1px", sm: "3px", md: "4px", lg: "5px", xl: "8px" },
                            "&:hover": {
                              backgroundColor: (theme) =>
                                theme.palette.primary.main + "20",
                            },
                            "& .MuiSvgIcon-root": {
                              fontSize: { xs: "16px", sm: "14px", md: "16px", lg: "18px", xl: "24px" },
                            },
                          }}
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                      <Button
                        variant="outlined"
                        color="default"
                        endIcon={
                          <SendIcon
                            sx={{
                              color: (theme) => theme.palette.primary.main,
                              fontSize: { xs: "6px", sm: "12px", md: "13px", lg: "14px", xl: "18px" },
                            }}
                          />
                        }
                        onClick={() => handleSendMessage(contact)}
                        sx={{
                          textTransform: "none",
                          borderRadius: { xs: "2rem", sm: "2rem", md: "2rem", lg: "2rem", xl: "2rem" },
                          border: (theme) =>
                            `${isXl ? "2px" : isXs ? "1px" : "2px"} solid ${theme.palette.primary.main}`,
                          color: (theme) => theme.palette.primary.main,
                          fontSize: { xs: "9px", sm: "8px", md: "9px", lg: "10px", xl: "12px" },
                          padding: { xs: "1px 3px", sm: "3px 6px", md: "4px 8px", lg: "5px 10px", xl: "8px 16px" },
                          minHeight: { xs: "18px", sm: "24px", md: "26px", lg: "28px", xl: "36px" },
                          whiteSpace: { xs: "nowrap", sm: "normal" },
                          "&:hover": {
                            backgroundColor: (theme) =>
                              theme.palette.primary.main,
                            color: (theme) => theme.palette.primary.contrastText,
                            borderColor: (theme) => theme.palette.primary.main,
                          },
                          "&:hover .MuiSvgIcon-root": {
                            color: (theme) => theme.palette.primary.contrastText,
                          },
                        }}
                      >
                        {isXl ? "Send Message" : "Send"}
                      </Button>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ padding: { xs: "6px 6px", sm: "8px 6px", md: "8px 4px", lg: "10px 5px", xl: "16px 8px" }, verticalAlign: "middle" }}>
                    {Array.isArray(contact.labels) &&
                    contact.labels.length > 0 ? (
                      <Box
                        display="flex"
                        flexWrap={{ xs: "nowrap", sm: "wrap", md: "wrap" }}
                        gap={{ xs: 0.25, sm: 0.3, md: 0.4, lg: 0.5, xl: 0.5 }}
                        alignItems="center"
                      >
                        {contact.labels.slice(0, 2).map((label, index) => (
                          <Chip
                            key={index}
                            label={
                              <HighlightedText
                                text={label}
                                searchTerm={searchTerm}
                              />
                            }
                            sx={{
                              bgcolor: (theme) =>
                                theme.palette.mode === "dark"
                                  ? "rgba(255, 255, 255, 0.12)"
                                  : "#f0f0f0",
                              fontSize: { xs: "12px", sm: "11px", md: "8px", lg: "9px", xl: "0.75rem" },
                              height: { xs: "24px", sm: "26px", md: "20px", lg: "22px", xl: "24px" },
                              borderRadius: { xs: "8px", sm: "9px", md: "10px", lg: "11px", xl: "12px" },
                              color: (theme) => theme.palette.text.primary,
                            }}
                            size="small"
                          />
                        ))}
                        {contact.labels.length > 2 && (
                          <Tooltip
                            title={
                              <Box
                                sx={{
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: 0.5,
                                }}
                              >
                                {contact.labels.slice(2).map((label, index) => (
                                  <Chip
                                    key={index}
                                    label={
                                      <HighlightedText
                                        text={label}
                                        searchTerm={searchTerm}
                                      />
                                    }
                                    sx={{
                                      bgcolor: (theme) =>
                                        theme.palette.mode === "dark"
                                          ? "rgba(255, 255, 255, 0.12)"
                                          : "#f0f0f0",
                                      fontSize: "0.75rem",
                                      height: "24px",
                                      borderRadius: "12px",
                                      color: (theme) =>
                                        theme.palette.text.primary,
                                    }}
                                    size="small"
                                  />
                                ))}
                              </Box>
                            }
                            arrow
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                color: (theme) => theme.palette.text.secondary,
                                fontWeight: "500",
                                ml: 0.5,
                                cursor: "pointer",
                                fontSize: { xs: "12px", sm: "11px", md: "0.75rem" },
                                whiteSpace: { xs: "nowrap", sm: "normal" },
                                "&:hover": {
                                  textDecoration: "underline",
                                },
                              }}
                            >
                              +{contact.labels.length - 2} more
                            </Typography>
                          </Tooltip>
                        )}
                      </Box>
                    ) : (
                      <Typography
                        variant="body2"
                        color="textSecondary"
                        sx={{ 
                          color: (theme) => theme.palette.text.secondary,
                          fontSize: { xs: "12px", sm: "11px", md: "9px", lg: "10px", xl: "0.875rem" },
                          whiteSpace: { xs: "nowrap", sm: "normal" },
                        }}
                      >
                        No labels
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ padding: { xs: "6px 6px", sm: "8px 6px", md: "8px 4px", lg: "10px 5px", xl: "16px 8px" }, verticalAlign: "middle" }}>
                    <Box
                      sx={{
                        display: "inline-block",
                        backgroundColor: (theme) => theme.palette.primary.main,
                        color: (theme) => theme.palette.primary.contrastText,
                        borderRadius: { xs: "0.2rem", sm: "0.3rem", md: "0.4rem", lg: "0.5rem", xl: "1rem" },
                        padding: { xs: "0.1rem 0.3rem", sm: "0.15rem 0.4rem", md: "0.15rem 0.5rem", lg: "0.15rem 0.5rem", xl: "0.25rem 0.75rem" },
                        fontSize: { xs: "12px", sm: "11px", md: "9px", lg: "10px", xl: "12px" },
                        whiteSpace: { xs: "nowrap", sm: "normal" },
                        lineHeight: { xs: 1.2, sm: 1.43 },
                      }}
                    >
                      {formatDate(contact.createdAt)}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ padding: { xs: "6px 4px", sm: "6px 2px", md: "8px 4px", lg: "10px 5px", xl: "16px 8px" }, textAlign: "center", verticalAlign: "middle", display: { xs: "none", sm: "table-cell" } }}>
                    <Box sx={{ display: "flex", gap: { xs: 0.15, sm: 0.4, md: 0.5, lg: 0.6, xl: 1 }, alignItems: "center", justifyContent: "center", flexWrap: { xs: "nowrap", sm: "wrap", lg: "nowrap" } }}>
                      <Tooltip title="View/Edit Contact">
                        <IconButton
                          size={isXl ? "medium" : "small"}
                          onClick={() => handleEditClick(contact)}
                          sx={{
                            color: (theme) => theme.palette.primary.main,
                            padding: { xs: "1px", sm: "3px", md: "4px", lg: "5px", xl: "8px" },
                            "&:hover": {
                              backgroundColor: (theme) =>
                                theme.palette.primary.main + "20",
                            },
                            "& .MuiSvgIcon-root": {
                              fontSize: { xs: "16px", sm: "14px", md: "16px", lg: "18px", xl: "24px" },
                            },
                          }}
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                      <Button
                        variant="outlined"
                        color="default"
                        endIcon={
                          <SendIcon
                            sx={{ 
                              color: (theme) => theme.palette.primary.main,
                              fontSize: { xs: "6px", sm: "12px", md: "13px", lg: "14px", xl: "18px" },
                            }}
                          />
                        }
                        onClick={() => handleSendMessage(contact)}
                        sx={{
                          textTransform: "none",
                          borderRadius: { xs: "2rem", sm: "2rem", md: "2rem", lg: "2rem", xl: "2rem" },
                          border: (theme) =>
                            `${isXl ? "2px" : isXs ? "1px" : "2px"} solid ${theme.palette.primary.main}`,
                          color: (theme) => theme.palette.primary.main,
                          fontSize: { xs: "9px", sm: "8px", md: "9px", lg: "10px", xl: "12px" },
                          padding: { xs: "1px 3px", sm: "3px 6px", md: "4px 8px", lg: "5px 10px", xl: "8px 16px" },
                          minHeight: { xs: "18px", sm: "24px", md: "26px", lg: "28px", xl: "36px" },
                          whiteSpace: { xs: "nowrap", sm: "normal" },
                          "&:hover": {
                            backgroundColor: (theme) =>
                              theme.palette.primary.main,
                            color: (theme) => theme.palette.primary.contrastText,
                            borderColor: (theme) => theme.palette.primary.main,
                          },
                          "&:hover .MuiSvgIcon-root": {
                            color: (theme) => theme.palette.primary.contrastText,
                          },
                        }}
                      >
                        {isXl ? "Send Message" : "Send"}
                      </Button>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ padding: { xs: "6px 6px", sm: "8px 6px", md: "8px 4px", lg: "10px 5px", xl: "16px 8px" }, verticalAlign: "middle" }}>
                    <Box sx={{ display: "flex", flexDirection: { xs: "row", sm: "column" }, flexWrap: { xs: "nowrap", sm: "wrap" }, gap: { xs: 0.5, sm: 0.4, md: 0.5, lg: 0.6, xl: 1 }, alignItems: { xs: "center", sm: "stretch" } }}>
                      {/* Display existing groups */}
                      {contact.groups && contact.groups.length > 0 ? (
                        <Box sx={{ display: "flex", gap: { xs: 0.2, sm: 0.3, md: 0.4, lg: 0.5, xl: 0.5 }, flexWrap: { xs: "nowrap", sm: "wrap" }, alignItems: "center" }}>
                          {contact.groups.slice(0, 2).map((groupId) => {
                            const group = groups.find(g => g._id === groupId);
                            return group ? (
                              <Chip
                                key={groupId}
                                label={group.name}
                                size="small"
                                variant="outlined"
                                sx={(theme) => ({
                                  color: theme.palette.text.primary,
                                  borderColor:
                                    theme.palette.mode === "dark"
                                      ? "rgba(255, 255, 255, 0.2)"
                                      : theme.palette.divider,
                                  backgroundColor: "transparent",
                                  fontSize: { xs: "12px", sm: "11px", md: "8px", lg: "9px", xl: "0.75rem" },
                                  height: { xs: "24px", sm: "26px", md: "20px", lg: "22px", xl: "24px" },
                                  padding: { xs: "0px 3px", sm: "0px 6px", md: "0px 8px", lg: "0px 10px", xl: "0px 12px" },
                                  fontWeight: 400,
                                  "& .MuiChip-label": {
                                    color: theme.palette.text.primary,
                                    px: { xs: 0.5, sm: 0.75 },
                                  },
                                  "&:hover": {
                                    backgroundColor: theme.palette.action.hover,
                                    borderColor:
                                      theme.palette.mode === "dark"
                                        ? "rgba(255, 255, 255, 0.28)"
                                        : theme.palette.text.secondary,
                                  },
                                })}
                              />
                            ) : null;
                          })}
                          {contact.groups.length > 2 && (
                            <Typography variant="caption" color="textSecondary" sx={{ fontSize: { xs: "12px", sm: "11px", md: "9px", lg: "10px", xl: "0.75rem" }, whiteSpace: { xs: "nowrap", sm: "normal" } }}>
                              +{contact.groups.length - 2} more
                            </Typography>
                          )}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="textSecondary" sx={{ fontSize: { xs: "12px", sm: "11px", md: "9px", lg: "10px", xl: "0.875rem" }, whiteSpace: { xs: "nowrap", sm: "normal" } }}>
                          No groups
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 15, 20, 25]}
          component="div"
          count={pagination.totalContacts || 0}
          rowsPerPage={Math.min(Math.max(pagination.limit || 10, 5), 25)}
          page={(pagination.currentPage || 1) - 1} // Convert back to 0-based indexing for MUI
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          sx={{
            flexShrink: 0,
            borderTop: (theme) => `1px solid ${theme.palette.divider}`,
            borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
            backgroundColor: (theme) => theme.palette.background.paper,
            boxShadow: (theme) =>
              theme.palette.mode === "dark"
                ? "0 -2px 8px rgba(0, 0, 0, 0.35), 0 2px 10px rgba(0, 0, 0, 0.35)"
                : "0 -1px 6px rgba(0, 0, 0, 0.08), 0 2px 8px rgba(0, 0, 0, 0.06)",
            color: (theme) => theme.palette.text.primary,
            fontSize: { xs: "7px", sm: "9px", md: "10px", lg: "11px", xl: "14px" },
            "& .MuiTablePagination-select": {
              color: "text.primary",
              fontSize: { xs: "7px", sm: "9px", md: "10px", lg: "11px", xl: "14px" },
              padding: { xs: "1px 12px 1px 3px", sm: "3px 20px 3px 6px", md: "4px 24px 4px 8px", lg: "5px 28px 5px 10px", xl: "6px 32px 6px 14px" },
            },
            "& .MuiTablePagination-selectIcon": {
              color: "text.secondary",
              fontSize: { xs: "10px", sm: "14px", md: "16px", lg: "18px", xl: "20px" },
            },
            "& .MuiTablePagination-displayedRows": {
              color: "text.secondary",
              fontSize: { xs: "7px", sm: "9px", md: "10px", lg: "11px", xl: "14px" },
            },
            "& .MuiTablePagination-toolbar": {
              minHeight: { xs: "28px", sm: "40px", md: "44px", lg: "48px", xl: "64px" },
              paddingLeft: { xs: "2px", sm: "6px", md: "8px", lg: "10px", xl: "16px" },
              paddingRight: { xs: "2px", sm: "6px", md: "8px", lg: "10px", xl: "16px" },
              flexWrap: { xs: "wrap", sm: "nowrap" },
            },
            "& .MuiTablePagination-spacer": {
              flex: { xs: "0 0 0", sm: "1 1 100%" },
            },
            "& .MuiIconButton-root": {
              color: "text.primary",
              padding: { xs: "1px", sm: "3px", md: "4px", lg: "5px", xl: "8px" },
              "& .MuiSvgIcon-root": {
                fontSize: { xs: "10px", sm: "14px", md: "16px", lg: "18px", xl: "24px" },
              },
            },
            "& .Mui-disabled": {
              color: "text.disabled",
            },
          }}
        />
      </Paper>
      
      {/* Labels Dialog */}
      <LabelsDialog
        open={labelsDialogOpen}
        onClose={() => setLabelsDialogOpen(false)}
        contactIds={selectedContacts}
        isBulkOperation={true}
        onSuccess={handleLabelsDialogSuccess}
      />

      {/* Edit Contact Modal */}
      <Dialog open={editModalOpen} onClose={() => setEditModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Contact</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <TextField
              label="Name"
              value={editFormData.name}
              onChange={(e) =>
                setEditFormData({ ...editFormData, name: e.target.value })
              }
              fullWidth
              required
            />
            <TextField
              label={editingContact?.platform === "facebook" ? "Facebook" : editingContact?.platform === "instagram" ? "Instagram" : "Phone Number"}
              value={editingContact?.platform === "facebook" ? "Facebook" : editingContact?.platform === "instagram" ? "Instagram" : editFormData.phoneNumberID}
              onChange={(e) =>
                editingContact?.platform !== "instagram" &&
                editingContact?.platform !== "facebook" &&
                setEditFormData({ ...editFormData, phoneNumberID: e.target.value })
              }
              fullWidth
              required={editingContact?.platform !== "instagram" && editingContact?.platform !== "facebook"}
              disabled={editingContact?.platform === "instagram" || editingContact?.platform === "facebook"}
              inputProps={editingContact?.platform === "instagram" || editingContact?.platform === "facebook" ? { readOnly: true } : undefined}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditModalOpen(false)}>Cancel</Button>
          <Button
            onClick={handleEditSubmit}
            variant="contained"
            disabled={
              !editFormData.name.trim() ||
              (editingContact?.platform !== "instagram" && editingContact?.platform !== "facebook" && !editFormData.phoneNumberID.trim())
            }
          >
            Update Contact
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Toast */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="success"
          sx={{ width: "100%", maxWidth: 480 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Floating Create Contact Button for xs screens */}
      {isXs && (
        <Box
          sx={{
            position: "fixed",
            bottom: 70, // Moved up from 16
            right: 16,
            zIndex: 1000,
          }}
        >
          <Tooltip title={ContactPermissions.Add ? "Create Contact" : "You don't have permission to create contacts"}>
            <span>
              <Button
                variant="contained"
                onClick={handleCreateContact}
                disabled={!ContactPermissions.Add}
                sx={{
                  borderRadius: "50%",
                  minWidth: "48px",
                  width: "48px",
                  height: "48px",
                  padding: 0,
                  boxShadow: 3,
                  "&:hover": {
                    boxShadow: 6,
                  },
                  "&.Mui-disabled": {
                    backgroundColor: (theme) => theme.palette.action.disabledBackground,
                    color: (theme) => theme.palette.action.disabled,
                  },
                }}
              >
                <AddIcon sx={{ fontSize: "24px" }} />
              </Button>
            </span>
          </Tooltip>
        </Box>
      )}

      {/* Create Contact Modal */}
      <AddContact 
        isOpen={isCreateContactModalOpen} 
        onClose={() => {
          setIsCreateContactModalOpen(false);
          // Refresh contacts list with current filters
          dispatch(fetchContacts({ 
            page: pagination.currentPage, 
            limit: pagination.limit,
            search: searchTerm,
            status: statusFilter,
            labels: labelFilter,
            sortField,
            sortDirection
          }));
        }} 
      />
    </Box>
  );
};

export default ContactsList;
